import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { CardNB, ButtonNB, BadgeNB, IconNB, SelectNB } from '../../../components/ui-nb';
import StatusDropdown from '../../../components/technician/StatusDropdown';
import StatusTimeline from '../../../components/technician/StatusTimeline';
import NotificationHistoryCard from '../../../components/NotificationHistoryCard';
import DeleteOrderModal from '../../../components/DeleteOrderModal';
import OrderReceiptActions from '../../../components/orders/OrderReceiptActions';
import SparePartStatusBadge from '../../../components/spare-parts/SparePartStatusBadge';
import CreateSparePartModal from '../../../components/spare-parts/CreateSparePartModal';
import OrderCommentThread from '../../../components/orders/OrderCommentThread';
import OrderExpenseList from '../../../components/orders/OrderExpenseList';
import { useAuthStore } from '../../../stores/authStore';
import { useToast } from '../../../components/ToastNB';
import type { OrderReceiptData } from '../../../lib/order-pdf';
import { getErrorMessage } from '../../../lib/errors';
import type { Tables } from '@tellowup/db';

interface OrderDetail {
  id: string;
  release_code: string;
  customer_name: string;
  customer_contact: string | null;
  device: string;
  device_brand: string | null;
  device_model: string | null;
  imei_serial: string | null;
  issue: string | null;
  failure_description: string | null;
  status: string;
  status_id: string;
  priority: string | null;
  estimated_date: string | null;
  created_at: string;
  branch_id: string;
  assigned_to: string | null;
  branches: { id: string; name: string } | null;
  profiles?: { id: string; full_name: string } | null;
  status_config?: { id: string; name: string; position: number } | null;
  status_history?: Array<{
    id: string;
    status_id: string;
    created_at: string;
    changed_by: string | null;
    status_config?: { id: string; name: string; position: number };
  }>;
  order_photo: { id: string; url: string; type: string }[] | null;
}

interface Technician {
  id: string;
  full_name: string;
}

interface StatusOption {
  id: string;
  name: string;
  position: number;
}

export default function OrderDetailPage() {
  const { id, lang } = useParams();
  const navigate = useNavigate();
  const currentUser = useAuthStore(s => s.user);
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [availableStatuses, setAvailableStatuses] = useState<StatusOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateSparePart, setShowCreateSparePart] = useState(false);
  const [spareParts, setSpareParts] = useState<Tables<'spare_parts'>[]>([]);
  const { addToast } = useToast();

  const refetch = () => setRefetchTrigger(prev => prev + 1);

  const handleOrderDeleted = () => {
    navigate(`/${lang}/admin/orders`);
  };

  useEffect(() => {
    if (!id) return;
    
    async function fetchOrder() {
      try {
        const { data, error: fetchError } = await supabase
          .from('orders')
          .select(`
            *,
            branches(id, name),
            profiles(id, full_name),
            status_config(id, name, position),
            status_history(id, status_id, created_at, changed_by, status_config(id, name, position)),
            order_photo(id, url, type)
          `)
          .eq('id', id)
          .single();
        if (fetchError) throw fetchError;
        setOrder(data as OrderDetail);
      } catch (err: unknown) {
        setError(getErrorMessage(err) || 'Failed to load order');
      } finally {
        setLoading(false);
      }
    }
    
    fetchOrder();
  }, [id, refetchTrigger]);

  // Fetch spare parts linked to this order
  useEffect(() => {
    if (!id) return;

    async function fetchSpareParts() {
      const { data } = await supabase
        .from('spare_parts')
        .select('*')
        .eq('order_id', id)
        .order('created_at', { ascending: false });

      if (data) {
        setSpareParts(data);
      }
    }

    fetchSpareParts();
  }, [id, refetchTrigger]);

  // Fetch technicians for assignment dropdown
  useEffect(() => {
    if (!order?.branches?.id) return;
    
    async function fetchTechnicians() {
      const { data } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('role', 'technician')
        .eq('organization_id', currentUser?.organizationId || '');
      
      if (data) {
        setTechnicians(data as Technician[]);
      }
    }
    
    fetchTechnicians();
  }, [order?.branches?.id, currentUser?.organizationId]);

  // Fetch available statuses
  useEffect(() => {
    if (!order?.branch_id) return;
    const branchId = order.branch_id;

    async function fetchStatuses() {
      const { data } = await supabase
        .from('status_config')
        .select('id, name, position')
        .eq('branch_id', branchId)
        .order('position', { ascending: true });

      if (data) {
        setAvailableStatuses(data as StatusOption[]);
      }
    }

    fetchStatuses();
  }, [order?.branch_id]);

  const handleStatusChange = async (newStatusId: string) => {
    if (!order || !currentUser?.id) return;
    
    const { error: invokeError } = await supabase.functions.invoke('notify-status-change', {
      body: {
        order_id: order.id,
        new_status_id: newStatusId,
        changed_by: currentUser.id,
      },
    });
    
    if (!invokeError) {
      refetch();
    }
  };

  const handleTechnicianAssign = async (techId: string) => {
    if (!order) return;
    
    const { error: updateError } = await supabase
      .from('orders')
      .update({ assigned_to: techId })
      .eq('id', order.id);
    
    if (!updateError) {
      refetch();
    }
  };

  if (loading) return <p className="text-nb-text-muted p-8">Loading order...</p>;
  if (error) return <p className="text-nb-red p-8">{error}</p>;
  if (!order) return <p className="text-nb-text-muted p-8">Order not found</p>;

  const currentStatusPosition = order.status_config?.position ?? 0;
  const canChangeStatus = currentStatusPosition < 6; // position 6+ are terminal

  // Compute spare parts blocking status
  const pendingSpareParts = spareParts.filter(
    (p) => p.status === 'sin_pedir' || p.status === 'pedido'
  );
  const hasPendingSpareParts = pendingSpareParts.length > 0;
  const BLOCKED_STATUS_NAMES = ['In repair', 'In testing', 'Ready for delivery', 'Delivered'];
  const blockedStatuses = hasPendingSpareParts
    ? BLOCKED_STATUS_NAMES.map(name => ({
        statusName: name,
        reason: `${pendingSpareParts.length} spare part(s) not received`,
      }))
    : [];

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6 flex items-center gap-3">
        <ButtonNB variant="secondary" size="sm" onClick={() => navigate(-1)}>
          <IconNB name="chevronLeft" size={16} />
          Back
        </ButtonNB>
        <h1 className="text-2xl font-bold text-nb-black flex-1">
          Order #{order.release_code}
        </h1>
        <ButtonNB variant="danger" size="sm" onClick={() => setShowDeleteModal(true)}>
          <IconNB name="trash" size={16} />
          Delete Order
        </ButtonNB>
      </div>

      <div className="flex flex-col gap-6">
        {/* Spare Parts Pending Warning Banner */}
        {hasPendingSpareParts && (
          <CardNB className="p-4 border-3 border-nb-yellow bg-nb-yellow/10">
            <div className="flex items-start gap-3">
              <span className="text-xl mt-0.5">⚠️</span>
              <div>
                <p className="font-nb font-bold text-nb-black text-sm mb-1">
                  Spare parts pending — cannot start repair
                </p>
                <p className="text-sm text-nb-text-muted">
                  {pendingSpareParts.length} spare part(s) still not received.{' '}
                   {pendingSpareParts.some((p) => p.status === 'sin_pedir')
                     ? 'You need to order the part(s) marked as "Not ordered" first.'
                     : 'Waiting for ordered parts to arrive.'}
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {pendingSpareParts.map((part) => (
                    <Link
                      key={part.id}
                      to={`/${lang}/admin/spare-parts/${part.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-white border-2 border-nb-black text-xs font-medium hover:bg-nb-yellow transition-colors"
                    >
                      <SparePartStatusBadge status={part.status} />
                      {part.description}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </CardNB>
        )}

        {/* Customer Section */}
        <CardNB className="p-6">
          <h2 className="text-sm font-nb font-bold text-nb-text-muted uppercase mb-3">Customer</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-nb-text-muted mb-1">Name</p>
              <p className="font-nb font-medium text-nb-black">{order.customer_name}</p>
            </div>
            <div>
              <p className="text-xs text-nb-text-muted mb-1">Contact</p>
              <p className="text-nb-black">{order.customer_contact || '—'}</p>
            </div>
          </div>
        </CardNB>

        {/* Equipment Section */}
        <CardNB className="p-6">
          <h2 className="text-sm font-nb font-bold text-nb-text-muted uppercase mb-3">Equipment</h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-nb-text-muted mb-1">Type</p>
              <p className="font-nb font-medium text-nb-black">{order.device}</p>
            </div>
            {order.device_brand && (
              <div>
                <p className="text-xs text-nb-text-muted mb-1">Brand</p>
                <p className="text-nb-black">{order.device_brand}</p>
              </div>
            )}
            {order.device_model && (
              <div>
                <p className="text-xs text-nb-text-muted mb-1">Model</p>
                <p className="text-nb-black">{order.device_model}</p>
              </div>
            )}
          </div>
        </CardNB>

        {/* Details Section */}
        <CardNB className="p-6">
          <h2 className="text-sm font-nb font-bold text-nb-text-muted uppercase mb-3">Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-nb-text-muted mb-1">Status</p>
              {canChangeStatus ? (
                <StatusDropdown
                  orderId={order.id}
                  currentStatusId={order.status_id}
                  availableStatuses={availableStatuses}
                  changedBy={currentUser?.id || ''}
                  onSuccess={refetch}
                  blockedStatuses={blockedStatuses}
                />
              ) : (
                <BadgeNB variant="info">
                  {order.status_config?.name || order.status}
                </BadgeNB>
              )}
            </div>
            <div>
              <p className="text-xs text-nb-text-muted mb-1">Priority</p>
              <p className={order.priority === 'urgent' ? 'text-nb-red font-nb font-bold' : 'text-nb-black'}>
                {order.priority || 'normal'}
              </p>
            </div>
            <div>
              <p className="text-xs text-nb-text-muted mb-1">Branch</p>
              <p className="text-nb-black">{order.branches?.name || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-nb-text-muted mb-1">Created</p>
              <p className="text-nb-black">{new Date(order.created_at).toLocaleDateString()}</p>
            </div>
            {order.estimated_date && (
              <div>
                <p className="text-xs text-nb-text-muted mb-1">Estimated delivery</p>
                <p className="text-nb-black">{order.estimated_date}</p>
              </div>
            )}
          </div>
        </CardNB>

        {/* Technician Assignment Section */}
        <CardNB className="p-6">
          <h2 className="text-sm font-nb font-bold text-nb-text-muted uppercase mb-3">Technician</h2>
          <div className="flex items-center gap-4">
            <SelectNB
              value={order.assigned_to || ''}
              onChange={(e) => handleTechnicianAssign(e.target.value)}
              className="flex-1"
            >
              <option value="">Unassigned</option>
              {technicians.map(tech => (
                <option key={tech.id} value={tech.id}>{tech.full_name}</option>
              ))}
            </SelectNB>
          </div>
          {order.profiles && (
            <div className="mt-3">
              <span className="text-nb-black font-nb text-sm font-bold">
                Assigned to: {order.profiles.full_name}
              </span>
            </div>
          )}
        </CardNB>

        {/* Spare Parts Section */}
        <CardNB className="p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-nb font-bold text-nb-text-muted uppercase">Spare Parts</h2>
            <ButtonNB variant="primary" size="sm" onClick={() => setShowCreateSparePart(true)}>
              <IconNB name="plus" size={16} />
              Crear refacción
            </ButtonNB>
          </div>
          {spareParts.length === 0 ? (
            <p className="text-nb-text-muted text-sm">No spare parts registered</p>
          ) : (
            <div className="space-y-2">
              {spareParts.map(part => (
                <div
                  key={part.id}
                  className="flex items-center justify-between py-2 border-b border-nb-black/10 last:border-b-0"
                >
                  <div className="flex-1">
                    <p className="font-nb font-medium text-nb-black text-sm">{part.description}</p>
                    {part.tracking_number && (
                      <p className="text-xs text-nb-text-muted mt-0.5">Track: {part.tracking_number}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <SparePartStatusBadge status={part.status} />
                    <Link
                      to={`/${lang}/admin/spare-parts/${part.id}`}
                      className="text-xs font-nb font-bold text-nb-black hover:underline"
                    >
                      View
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardNB>

        {/* Comment Thread */}
        <OrderCommentThread orderId={order.id} />

        {/* Extra Expenses */}
        <OrderExpenseList orderId={order.id} />

        {/* Status History Timeline */}
        <CardNB className="p-6">
          <h2 className="text-sm font-nb font-bold text-nb-text-muted uppercase mb-3">Status History</h2>
          <StatusTimeline 
            history={order.status_history || []} 
            isLoading={loading}
          />
        </CardNB>

        {/* Notification History */}
        <NotificationHistoryCard orderId={order.id} />

        {/* Client Tracking — QR + Download PDF + Print */}
        {order.release_code && (
          <OrderReceiptActions
            order={
              {
                release_code: order.release_code,
                customer_name: order.customer_name,
                customer_contact: order.customer_contact,
                device: order.device,
                device_brand: order.device_brand,
                device_model: order.device_model,
                imei_serial: order.imei_serial,
                issue: order.issue,
                status_name: order.status_config?.name || order.status,
                branch_name: order.branches?.name,
                org_name: 'TellowUp',
                created_at: order.created_at,
              } satisfies OrderReceiptData
            }
          />
        )}

        {/* Issue Section */}
        {(order.issue || order.failure_description) && (
          <CardNB className="p-6">
            <h2 className="text-sm font-nb font-bold text-nb-text-muted uppercase mb-3">Issue</h2>
            <p className="text-nb-black whitespace-pre-wrap">{order.issue || order.failure_description}</p>
          </CardNB>
        )}

        {/* Photos Section */}
        {order.order_photo && order.order_photo.length > 0 && (
          <CardNB className="p-6">
            <h2 className="text-sm font-nb font-bold text-nb-text-muted uppercase mb-3">Photos</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {order.order_photo.map(photo => (
                <img key={photo.id} src={photo.url} alt="Order photo" className="rounded-[2px] border-3 border-nb-black object-cover w-full h-40" />
              ))}
            </div>
          </CardNB>
        )}
      </div>

      <DeleteOrderModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        orderId={order.id}
        orderCode={order.release_code}
        onDeleted={handleOrderDeleted}
      />

      <CreateSparePartModal
        isOpen={showCreateSparePart}
        onClose={() => setShowCreateSparePart(false)}
        orderId={order.id}
        onCreated={() => {
          setShowCreateSparePart(false);
          refetch();
        }}
      />
    </div>
  );
}
